/**
 * Tab Navigation System - State Reducer
 *
 * Handles all state transitions for the tabs system.
 */

import { DEFAULT_TAB_CONFIG } from '@/config/tabsConfig';
import type { PaneId, TabHistoryEntry, TabItem, TabPane, TabsAction, TabsConfig, TabsState } from './types';
import { createTabItem, findTabByPath, generateTabKey, getBasePath, getSearchFromPath, restoreTabsState, restoreWindowLocalState } from './persistence';
import { loadUserSettings, mergeSettingsWithConfig } from './settings';

/**
 * Resolve the effective config by merging user settings with defaults.
 * Called once at init and re-read by the provider when settings change.
 */
export function getEffectiveConfig(): TabsConfig {
  const userSettings = loadUserSettings();
  return mergeSettingsWithConfig(DEFAULT_TAB_CONFIG, userSettings);
}

/**
 * Get the default tab (Dashboard)
 */
function getDefaultTab(): TabItem {
  return createTabItem('/dashboard', 'Dashboard', { pinned: true, pinnedOrder: 0 });
}

export const ROOT_PANE_ID: PaneId = 'root';

function isRootPane(paneId: PaneId | undefined): boolean {
  return !paneId || paneId === ROOT_PANE_ID;
}

function createPane(tabKeys: string[], activeTabKey: string | null = tabKeys[0] || null, id: PaneId = ROOT_PANE_ID): TabPane {
  return { id, tabKeys, activeTabKey };
}

function generatePaneId(): PaneId {
  return typeof crypto !== 'undefined' && crypto.randomUUID
    ? `pane-${crypto.randomUUID()}`
    : `pane-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
}

function ensurePaneState(state: TabsState): TabsState {
  if (state.panes?.length) {
    return state;
  }

  const tabKeys = state.tabs.map((tab) => tab.key);
  return {
    ...state,
    panes: [createPane(tabKeys, state.activeTabKey)],
    activePaneId: ROOT_PANE_ID,
    splitOrientation: 'vertical',
  };
}

function getPaneIdForTab(state: TabsState, tabKey: string): PaneId {
  return state.panes.find((pane) => pane.tabKeys.includes(tabKey))?.id || state.activePaneId;
}

function getPane(state: TabsState, paneId?: PaneId): TabPane | undefined {
  return state.panes.find((pane) => pane.id === (paneId || state.activePaneId)) || state.panes[0];
}

function getActiveTabKeyForPane(state: TabsState, paneId?: PaneId): string | null {
  return getPane(state, paneId)?.activeTabKey || state.activeTabKey;
}

function getActiveTabForPane(state: TabsState, paneId?: PaneId): TabItem | undefined {
  const activeTabKey = getActiveTabKeyForPane(state, paneId);
  return state.tabs.find((tab) => tab.key === activeTabKey);
}

function addTabToPane(state: TabsState, paneId: PaneId, tabKey: string, afterTabKey?: string): TabPane[] {
  return state.panes.map((pane) => {
    if (pane.id !== paneId) return pane;
    const tabKeys = [...pane.tabKeys];
    const insertIndex = afterTabKey ? tabKeys.indexOf(afterTabKey) + 1 : tabKeys.length;
    tabKeys.splice(insertIndex > 0 ? insertIndex : tabKeys.length, 0, tabKey);
    return { ...pane, tabKeys };
  });
}

function getFallbackTabKeyForPane(tabKeys: string[], removedTabKey: string): string | null {
  const removedIndex = tabKeys.findIndex((tabKey) => tabKey === removedTabKey);
  const remainingTabKeys = tabKeys.filter((tabKey) => tabKey !== removedTabKey);
  return remainingTabKeys[removedIndex - 1] || remainingTabKeys[removedIndex] || remainingTabKeys[0] || null;
}

function collapsePanes(state: TabsState, activeTabKey: string | null = state.activeTabKey): TabsState {
  const mergedTabKeys = state.panes.flatMap((pane) => pane.tabKeys)
    .filter((tabKey, index, tabKeys) => tabKeys.indexOf(tabKey) === index);
  const requestedActiveTab = state.tabs.find((tab) => tab.key === activeTabKey);
  const rootPane = state.panes.find((pane) => isRootPane(pane.id));
  const rootTabWithSameLocation = requestedActiveTab && rootPane
    ? rootPane.tabKeys.find((tabKey) => {
        if (tabKey === activeTabKey) return false;
        const tab = state.tabs.find((item) => item.key === tabKey);
        return tab ? getTabLocationKey(tab) === getTabLocationKey(requestedActiveTab) : false;
      })
    : null;
  const preferredActiveTabKey = rootTabWithSameLocation || activeTabKey;
  const fallbackActiveTabKey = activeTabKey && mergedTabKeys.includes(activeTabKey)
    ? preferredActiveTabKey
    : mergedTabKeys[0] || state.tabs[0]?.key || null;

  return {
    ...state,
    activeTabKey: fallbackActiveTabKey,
    activePaneId: ROOT_PANE_ID,
    panes: [createPane(mergedTabKeys, fallbackActiveTabKey)],
    splitOrientation: 'vertical',
  };
}

function getTabLocationKey(tab: TabItem): string {
  const currentEntry = tab.history[tab.historyIndex];
  const path = currentEntry?.path || tab.path;
  const search = currentEntry?.search || '';
  return `${path}${search}`;
}

function duplicateTab(tab: TabItem): TabItem {
  const currentEntry = tab.history[tab.historyIndex];
  const currentPath = currentEntry?.path || tab.path;
  const currentSearch = currentEntry?.search || '';

  return {
    key: generateTabKey(currentPath),
    path: currentPath,
    title: tab.title,
    icon: tab.icon,
    closable: true,
    pinned: false,
    history: [{
      path: currentPath,
      search: currentSearch,
      state: currentEntry?.state,
      timestamp: Date.now(),
    }],
    historyIndex: 0,
    lastAccessed: Date.now(),
  };
}

function normalizePanes(tabs: TabItem[], panes: TabPane[] | undefined, activeTabKey: string | null): TabPane[] {
  const tabKeys = new Set(tabs.map((tab) => tab.key));
  const fallbackActiveTabKey = activeTabKey && tabKeys.has(activeTabKey)
    ? activeTabKey
    : tabs[0]?.key || null;
  const nextPanes = (panes || [])
    .map((pane) => {
      const paneTabKeys = pane.tabKeys.filter((key) => tabKeys.has(key));
      const paneActiveTabKey = pane.activeTabKey && paneTabKeys.includes(pane.activeTabKey)
        ? pane.activeTabKey
        : paneTabKeys[0] || null;
      return { ...pane, tabKeys: paneTabKeys, activeTabKey: paneActiveTabKey };
    })
    .filter((pane) => pane.tabKeys.length > 0);

  if (nextPanes.length > 0) {
    const rootPane = nextPanes.find((pane) => isRootPane(pane.id)) || nextPanes[0];
    const secondaryPanes = nextPanes.filter((pane) => pane !== rootPane);
    const normalizedRootPane = { ...rootPane, id: ROOT_PANE_ID };

    if (secondaryPanes.length === 0) {
      return [normalizedRootPane];
    }

    const [firstSecondaryPane, ...extraPanes] = secondaryPanes;
    const secondaryTabKeys = [
      ...firstSecondaryPane.tabKeys,
      ...extraPanes.flatMap((pane) => pane.tabKeys),
    ].filter((key, index, keys) => keys.indexOf(key) === index);
    const preferredSecondaryActiveTabKey = secondaryTabKeys.includes(firstSecondaryPane.activeTabKey || '')
      ? firstSecondaryPane.activeTabKey
      : secondaryTabKeys.includes(activeTabKey || '')
        ? activeTabKey
        : secondaryTabKeys[0] || null;

    return [
      normalizedRootPane,
      {
        ...firstSecondaryPane,
        tabKeys: secondaryTabKeys,
        activeTabKey: preferredSecondaryActiveTabKey,
      },
    ];
  }

  return [createPane(tabs.map((tab) => tab.key), fallbackActiveTabKey)];
}

function removeTabFromState(state: TabsState, tab: TabItem, config: TabsConfig): TabsState {
  const paneContainingTab = state.panes.find((pane) => pane.tabKeys.includes(tab.key));
  const fallbackActiveTabKey = state.activeTabKey === tab.key && paneContainingTab
    ? getFallbackTabKeyForPane(paneContainingTab.tabKeys, tab.key)
    : state.activeTabKey;
  const tabs = state.tabs.filter((item) => item.key !== tab.key);
  const panes = normalizePanes(tabs, state.panes, fallbackActiveTabKey);
  const activePaneId = panes.some((pane) => pane.id === state.activePaneId)
    ? state.activePaneId
    : ROOT_PANE_ID;
  const rootPane = panes.find((pane) => isRootPane(pane.id)) || panes[0];
  const activeTabKey = fallbackActiveTabKey && tabs.some((item) => item.key === fallbackActiveTabKey)
    ? fallbackActiveTabKey
    : rootPane?.activeTabKey || tabs[0]?.key || null;

  return {
    ...state,
    tabs,
    panes,
    activePaneId,
    activeTabKey,
    recentlyClosed: [tab, ...state.recentlyClosed].slice(0, config.maxRecentlyClosed),
  };
}

function restorePanesByPath(tabs: TabItem[], panePaths: NonNullable<ReturnType<typeof restoreWindowLocalState>>['panePaths']): TabPane[] | undefined {
  if (!panePaths?.length) {
    return undefined;
  }

  const tabKeysByPath = new Map<string, string[]>();
  for (const tab of tabs) {
    const keys = tabKeysByPath.get(tab.path) || [];
    keys.push(tab.key);
    tabKeysByPath.set(tab.path, keys);
  }

  const usedTabKeys = new Set<string>();
  const takeTabKeyForPath = (path: string): string | undefined => {
    const keys = tabKeysByPath.get(path) || [];
    const unusedKey = keys.find((key) => !usedTabKeys.has(key));
    const key = unusedKey || keys[0];
    if (key) {
      usedTabKeys.add(key);
    }
    return key;
  };

  return panePaths
    .map((pane) => {
      const tabKeys = pane.tabPaths
        .map((path) => takeTabKeyForPath(path))
        .filter((key): key is string => Boolean(key));
      const activePaneTabKey = pane.activeTabPath
        ? tabKeys.find((tabKey) => tabs.find((tab) => tab.key === tabKey)?.path === pane.activeTabPath) || tabKeys[0] || null
        : tabKeys[0] || null;

      return {
        id: pane.id,
        tabKeys,
        activeTabKey: activePaneTabKey,
      };
    })
    .filter((pane) => pane.tabKeys.length > 0);
}

function applyIndexedPaneActiveKeys(pathRestoredPanes: TabPane[] | undefined, indexRestoredPanes: TabPane[] | undefined): TabPane[] | undefined {
  if (!pathRestoredPanes?.length) {
    return indexRestoredPanes;
  }
  if (!indexRestoredPanes?.length) {
    return pathRestoredPanes;
  }

  const indexedPanesById = new Map(indexRestoredPanes.map((pane) => [pane.id, pane]));
  return pathRestoredPanes.map((pane) => {
    const indexedPane = indexedPanesById.get(pane.id);
    if (indexedPane?.activeTabKey && pane.tabKeys.includes(indexedPane.activeTabKey)) {
      return {
        ...pane,
        activeTabKey: indexedPane.activeTabKey,
      };
    }
    return pane;
  });
}

/**
 * Initial state for the tabs system
 * 
 * Multi-window support:
 * - Pinned tabs are shared across all windows (from restoreTabsState)
 * - Recent tabs are window-local (from restoreWindowLocalState)
 * - Each browser window has its own set of recent tabs
 */
export function getInitialState(): TabsState {
  const config = getEffectiveConfig();

  // 1. Restore shared state (pinned tabs from localStorage cache of backend preferences)
  const shared = restoreTabsState(config);
  const pinnedTabs = shared?.tabs.filter((t) => t.pinned) || [];

  // 2. Restore window-local state (recent tabs for this browser window)
  // Fallback to shared storage's recent tabs if window-local is empty
  // (happens when preferences context hasn't loaded yet before refresh)
  const windowLocal = restoreWindowLocalState(config);
  const recentTabs = windowLocal?.tabs?.length 
    ? windowLocal.tabs 
    : (shared?.tabs.filter((t) => !t.pinned) || []);

  // 3. Merge pinned + window-local recent tabs
  // Filter out any recent tabs that duplicate pinned tab paths
  const pinnedPaths = new Set(pinnedTabs.map((t) => t.path));
  const filteredRecentTabs = recentTabs.filter((t) => !pinnedPaths.has(t.path));

  const allTabs = [...pinnedTabs, ...filteredRecentTabs];

  if (allTabs.length > 0) {
    // Prefer window-local active tab, fallback to shared, then first tab
    const activeTabKey = windowLocal?.activeTabKey 
      || shared?.activeTabKey 
      || allTabs[0]?.key 
      || null;

    const sortedTabs = sortTabs(allTabs);
    const indexRestoredPanes = windowLocal?.panes?.length ? windowLocal.panes : undefined;
    const pathRestoredPanes = restorePanesByPath(sortedTabs, windowLocal?.panePaths);
    const panes = normalizePanes(sortedTabs, applyIndexedPaneActiveKeys(pathRestoredPanes, indexRestoredPanes), activeTabKey);
    const activePaneId = panes.some((pane) => pane.id === windowLocal?.activePaneId)
      ? windowLocal?.activePaneId || ROOT_PANE_ID
      : ROOT_PANE_ID;

    return {
      tabs: sortedTabs,
      activeTabKey,
      panes,
      activePaneId,
      splitOrientation: windowLocal?.splitOrientation || 'vertical',
      recentlyClosed: [],
    };
  }

  // Fallback to default tab
  const defaultTab = getDefaultTab();
  return {
    tabs: [defaultTab],
    activeTabKey: defaultTab.key,
    panes: [createPane([defaultTab.key], defaultTab.key)],
    activePaneId: ROOT_PANE_ID,
    splitOrientation: 'vertical',
    recentlyClosed: [],
  };
}

/**
 * Sort tabs with pinned tabs first (by pinnedOrder), then regular tabs
 */
function sortTabs(tabs: TabItem[]): TabItem[] {
  const pinned = tabs.filter((t) => t.pinned).sort((a, b) => (a.pinnedOrder || 0) - (b.pinnedOrder || 0));
  const regular = tabs.filter((t) => !t.pinned);
  return [...pinned, ...regular];
}

/**
 * Create a tabs reducer that uses the given config for limits.
 */
export function createTabsReducer(config: TabsConfig) {
  return function tabsReducer(state: TabsState, action: TabsAction): TabsState {
    return tabsReducerWithConfig(state, action, config);
  };
}

/**
 * Tabs reducer (default - reads user settings at dispatch time)
 */
export function tabsReducer(state: TabsState, action: TabsAction): TabsState {
  return tabsReducerWithConfig(state, action, getEffectiveConfig());
}

function tabsReducerWithConfig(state: TabsState, action: TabsAction, config: TabsConfig): TabsState {
  state = ensurePaneState(state);

  switch (action.type) {
    case 'OPEN_TAB': {
      const { path, title, icon, search, state: routeState, focus = true, forceNew = false } = action.payload;
      const requestedPaneId = action.payload.paneId || ROOT_PANE_ID;
      const targetPane = getPane(state, requestedPaneId);
      const targetPaneId = targetPane?.id || ROOT_PANE_ID;
      const shouldPromoteToPrimary = focus && isRootPane(targetPaneId);

      // Extract base path - path may contain embedded search params
      const basePath = getBasePath(path);
      // Determine effective search - prefer explicit search, fallback to search embedded in path
      const effectiveSearch = search || getSearchFromPath(path);
      const targetPaneTabKeys = new Set(targetPane?.tabKeys || []);

      // Check if tab already exists (skip if forceNew is true)
      if (!forceNew) {
        const existingTab = findTabByPath(
          state.tabs.filter((tab) => targetPaneTabKeys.has(tab.key)),
          basePath
        );
        if (existingTab) {
          const currentEntry = existingTab.history[existingTab.historyIndex];
          const currentSearch = currentEntry?.search || '';
          
          // If search params differ, add a new history entry to navigate within the existing tab
          if (effectiveSearch !== currentSearch) {
            const newHistory = existingTab.history.slice(0, existingTab.historyIndex + 1);
            const newEntry: TabHistoryEntry = {
              path: basePath,
              search: effectiveSearch,
              state: routeState,
              timestamp: Date.now(),
            };
            newHistory.push(newEntry);
            
            // Limit history size
            const maxHistorySize = 50;
            if (newHistory.length > maxHistorySize) {
              newHistory.shift();
            }
            
            return {
              ...state,
              tabs: state.tabs.map((t) =>
                t.key === existingTab.key
                  ? {
                      ...t,
                      path: basePath,
                      title: title || t.title,
                      history: newHistory,
                      historyIndex: newHistory.length - 1,
                      lastAccessed: Date.now(),
                    }
                  : t
              ),
              activeTabKey: shouldPromoteToPrimary ? existingTab.key : state.activeTabKey,
              activePaneId: shouldPromoteToPrimary ? targetPaneId : state.activePaneId,
              panes: state.panes.map((pane) =>
                focus && pane.tabKeys.includes(existingTab.key)
                  ? { ...pane, activeTabKey: existingTab.key }
                  : pane
              ),
            };
          }
          
          // Same search params - just update last accessed and optionally focus
          return {
            ...state,
            tabs: state.tabs.map((t) =>
              t.key === existingTab.key ? { ...t, lastAccessed: Date.now() } : t
            ),
            activeTabKey: shouldPromoteToPrimary ? existingTab.key : state.activeTabKey,
            activePaneId: shouldPromoteToPrimary ? targetPaneId : state.activePaneId,
            panes: state.panes.map((pane) =>
              focus && pane.tabKeys.includes(existingTab.key)
                ? { ...pane, activeTabKey: existingTab.key }
                : pane
            ),
          };
        }

        const existingTabInAnyPane = findTabByPath(state.tabs, basePath);
        if (existingTabInAnyPane) {
          const existingPaneId = getPaneIdForTab(state, existingTabInAnyPane.key);
          const currentEntry = existingTabInAnyPane.history[existingTabInAnyPane.historyIndex];
          const currentSearch = currentEntry?.search || '';

          if (effectiveSearch !== currentSearch) {
            const newHistory = existingTabInAnyPane.history.slice(0, existingTabInAnyPane.historyIndex + 1);
            const newEntry: TabHistoryEntry = {
              path: basePath,
              search: effectiveSearch,
              state: routeState,
              timestamp: Date.now(),
            };
            newHistory.push(newEntry);

            const maxHistorySize = 50;
            if (newHistory.length > maxHistorySize) {
              newHistory.shift();
            }

            return {
              ...state,
              tabs: state.tabs.map((t) =>
                t.key === existingTabInAnyPane.key
                  ? {
                      ...t,
                      path: basePath,
                      title: title || t.title,
                      history: newHistory,
                      historyIndex: newHistory.length - 1,
                      lastAccessed: Date.now(),
                    }
                  : t
              ),
              activeTabKey: focus && isRootPane(existingPaneId) ? existingTabInAnyPane.key : state.activeTabKey,
              activePaneId: focus ? existingPaneId : state.activePaneId,
              panes: state.panes.map((pane) =>
                focus && pane.tabKeys.includes(existingTabInAnyPane.key)
                  ? { ...pane, activeTabKey: existingTabInAnyPane.key }
                  : pane
              ),
            };
          }

          return {
            ...state,
            tabs: state.tabs.map((t) =>
              t.key === existingTabInAnyPane.key ? { ...t, lastAccessed: Date.now() } : t
            ),
            activeTabKey: focus && isRootPane(existingPaneId) ? existingTabInAnyPane.key : state.activeTabKey,
            activePaneId: focus ? existingPaneId : state.activePaneId,
            panes: state.panes.map((pane) =>
              focus && pane.tabKeys.includes(existingTabInAnyPane.key)
                ? { ...pane, activeTabKey: existingTabInAnyPane.key }
                : pane
            ),
          };
        }
      }

      // Check max tabs limit
      if (state.tabs.length >= config.maxTabs) {
        // Close oldest non-pinned tab
        const unpinnedTabs = state.tabs.filter((t) => !t.pinned);
        if (unpinnedTabs.length > 0) {
          const oldest = unpinnedTabs.reduce((a, b) =>
            a.lastAccessed < b.lastAccessed ? a : b
          );
          state = removeTabFromState(state, oldest, config);
        } else {
          // All tabs are pinned - refuse to open a new tab beyond the limit
          return state;
        }
      }

      const destinationPaneId = state.panes.some((pane) => pane.id === targetPaneId)
        ? targetPaneId
        : ROOT_PANE_ID;
      const destinationPane = getPane(state, destinationPaneId);
      const shouldPromoteDestinationToPrimary = focus && isRootPane(destinationPaneId);

      // Create new tab with base path (search params stored separately in history)
      const newTab = createTabItem(basePath, title, { icon, search: effectiveSearch, state: routeState });

      // Insert new tab right after the currently active tab
      const activeTabIndex = state.tabs.findIndex((t) => t.key === destinationPane?.activeTabKey);
      const insertIndex = activeTabIndex === -1 ? state.tabs.length : activeTabIndex + 1;
      
      const newTabs = [...state.tabs];
      newTabs.splice(insertIndex, 0, newTab);

      return {
        ...state,
        tabs: sortTabs(newTabs),
        panes: addTabToPane(state, destinationPaneId, newTab.key, destinationPane?.activeTabKey || undefined)
          .map((pane) => (focus && pane.id === destinationPaneId ? { ...pane, activeTabKey: newTab.key } : pane)),
        activePaneId: shouldPromoteDestinationToPrimary ? destinationPaneId : state.activePaneId,
        // Only focus new tab if focus=true (default), otherwise keep current tab focused
        activeTabKey: shouldPromoteDestinationToPrimary ? newTab.key : state.activeTabKey,
      };
    }

    case 'CLOSE_TAB': {
      const { key } = action.payload;
      const tabToClose = state.tabs.find((t) => t.key === key);
      const paneId = action.payload.paneId || getPaneIdForTab(state, key);
      const pane = getPane(state, paneId);

      // Can't close pinned tabs
      if (!tabToClose || tabToClose.pinned || !pane) {
        return state;
      }

      const newTabs = state.tabs.filter((t) => t.key !== key);
      let newActiveKey = state.activeTabKey;
      let newActivePaneId = state.activePaneId;
      let newPanes = state.panes.map((p) => {
        if (p.id !== paneId) return p;
        const paneTabKeys = p.tabKeys.filter((tabKey) => tabKey !== key);
        let paneActiveTabKey = p.activeTabKey;
        if (p.activeTabKey === key) {
          const closedIndex = p.tabKeys.findIndex((tabKey) => tabKey === key);
          paneActiveTabKey = paneTabKeys[closedIndex - 1] || paneTabKeys[closedIndex] || null;
        }
        return { ...p, tabKeys: paneTabKeys, activeTabKey: paneActiveTabKey };
      });

      // If closing active tab, focus previous tab (or next if no previous)
      if (state.activeTabKey === key) {
        const updatedPane = newPanes.find((p) => p.id === paneId);
        newActiveKey = updatedPane?.activeTabKey || null;
      }

      // Ensure at least one tab remains
      if (newTabs.length === 0) {
        const defaultTab = getDefaultTab();
        return {
          ...state,
          tabs: [defaultTab],
          activeTabKey: defaultTab.key,
          panes: [createPane([defaultTab.key], defaultTab.key)],
          activePaneId: ROOT_PANE_ID,
          splitOrientation: 'vertical',
          recentlyClosed: [tabToClose, ...state.recentlyClosed].slice(0, config.maxRecentlyClosed),
        };
      }

      const paneHasNoTabs = newPanes.find((p) => p.id === paneId)?.tabKeys.length === 0;
      if (paneHasNoTabs && newPanes.length > 1) {
        const removedPaneIndex = newPanes.findIndex((p) => p.id === paneId);
        newPanes = newPanes.filter((p) => p.id !== paneId);
        if (isRootPane(paneId) && newPanes.length > 0) {
          newPanes = [
            { ...newPanes[0], id: ROOT_PANE_ID },
            ...newPanes.slice(1),
          ];
        }
        const fallbackPane = newPanes[Math.max(0, removedPaneIndex - 1)] || newPanes[0];
        if (state.activePaneId === paneId || state.activeTabKey === key) {
          newActivePaneId = fallbackPane.id;
          newActiveKey = fallbackPane.activeTabKey;
        }
      } else if (state.activeTabKey === key) {
        newActivePaneId = paneId;
      }

      newPanes = normalizePanes(newTabs, newPanes, newActiveKey);
      const normalizedActivePane = newPanes.some((p) => p.id === newActivePaneId)
        ? newPanes.find((p) => p.id === newActivePaneId)
        : newPanes.find((p) => p.activeTabKey === newActiveKey) || newPanes[0];

      return {
        ...state,
        tabs: newTabs,
        activeTabKey: normalizedActivePane?.activeTabKey || newPanes[0]?.activeTabKey || newTabs[0]?.key || null,
        panes: newPanes,
        activePaneId: normalizedActivePane?.id || ROOT_PANE_ID,
        recentlyClosed: [tabToClose, ...state.recentlyClosed].slice(0, config.maxRecentlyClosed),
      };
    }

    case 'CLOSE_OTHER_TABS': {
      const { key, paneId } = action.payload;
      const keepTab = state.tabs.find((t) => t.key === key);
      if (!keepTab) return state;
      const sourcePane = paneId ? getPane(state, paneId) : state.panes.find((pane) => pane.tabKeys.includes(key));
      if (!sourcePane) return state;

      const paneTabKeys = new Set(sourcePane.tabKeys);
      const closedTabs = state.tabs.filter((t) => paneTabKeys.has(t.key) && t.key !== key && !t.pinned);
      const closedTabKeys = new Set(closedTabs.map((tab) => tab.key));
      const newTabs = state.tabs.filter((t) => !closedTabKeys.has(t.key));
      const nextActiveTabKey = closedTabKeys.has(state.activeTabKey || '') ? key : state.activeTabKey;
      const nextActivePaneId = closedTabKeys.has(state.activeTabKey || '') ? sourcePane.id : state.activePaneId;

      return {
        ...state,
        tabs: sortTabs(newTabs),
        activeTabKey: nextActiveTabKey,
        activePaneId: nextActivePaneId,
        panes: normalizePanes(newTabs, state.panes.map((pane) => ({
          ...pane,
          tabKeys: pane.id === sourcePane.id
            ? pane.tabKeys.filter((tabKey) => !closedTabKeys.has(tabKey))
            : pane.tabKeys,
          activeTabKey: pane.id === sourcePane.id
            ? key
            : pane.activeTabKey,
        })), nextActiveTabKey),
        recentlyClosed: [...closedTabs, ...state.recentlyClosed].slice(0, config.maxRecentlyClosed),
      };
    }

    case 'CLOSE_TABS_TO_RIGHT': {
      const { key, paneId } = action.payload;
      const sourcePane = paneId ? getPane(state, paneId) : state.panes.find((pane) => pane.tabKeys.includes(key));
      if (!sourcePane) return state;
      const tabIndex = sourcePane.tabKeys.findIndex((tabKey) => tabKey === key);
      if (tabIndex === -1) return state;

      const rightTabKeys = new Set(sourcePane.tabKeys.slice(tabIndex + 1));
      const closedTabs = state.tabs.filter((t) => rightTabKeys.has(t.key) && !t.pinned);
      const closedTabKeys = new Set(closedTabs.map((tab) => tab.key));
      const newTabs = state.tabs.filter((t) => !closedTabKeys.has(t.key));

      // If active tab was closed, focus the rightmost remaining tab
      let newActiveKey = state.activeTabKey;
      if (closedTabKeys.has(state.activeTabKey || '')) {
        newActiveKey = key;
      }

      return {
        ...state,
        tabs: sortTabs(newTabs),
        activeTabKey: newActiveKey,
        panes: normalizePanes(newTabs, state.panes.map((pane) => ({
          ...pane,
          tabKeys: pane.tabKeys.filter((tabKey) => !closedTabKeys.has(tabKey)),
          activeTabKey: pane.activeTabKey && !closedTabKeys.has(pane.activeTabKey)
            ? pane.activeTabKey
            : pane.tabKeys.find((tabKey) => !closedTabKeys.has(tabKey)) || null,
        })), newActiveKey),
        recentlyClosed: [...closedTabs, ...state.recentlyClosed].slice(0, config.maxRecentlyClosed),
      };
    }

    case 'CLOSE_ALL_TABS': {
      const pinnedTabs = state.tabs.filter((t) => t.pinned);
      const closedTabs = state.tabs.filter((t) => !t.pinned);

      let newTabs = pinnedTabs;
      if (newTabs.length === 0) {
        newTabs = [getDefaultTab()];
      }

      return {
        ...state,
        tabs: sortTabs(newTabs),
        activeTabKey: newTabs[0]?.key || null,
        panes: [createPane(newTabs.map((tab) => tab.key), newTabs[0]?.key || null)],
        activePaneId: ROOT_PANE_ID,
        splitOrientation: 'vertical',
        recentlyClosed: [...closedTabs, ...state.recentlyClosed].slice(0, config.maxRecentlyClosed),
      };
    }

    case 'FOCUS_TAB': {
      const { key } = action.payload;
      const tab = state.tabs.find((t) => t.key === key);
      if (!tab) return state;
      const paneId = action.payload.paneId || getPaneIdForTab(state, key);
      const shouldPromoteToPrimary = isRootPane(paneId);

      return {
        ...state,
        tabs: state.tabs.map((t) =>
          t.key === key ? { ...t, lastAccessed: Date.now() } : t
        ),
        activeTabKey: shouldPromoteToPrimary ? key : state.activeTabKey,
        activePaneId: paneId,
        panes: state.panes.map((pane) =>
          pane.id === paneId
            ? { ...pane, activeTabKey: key }
            : pane
        ),
      };
    }

    case 'FOCUS_PANE': {
      const pane = getPane(state, action.payload.paneId);
      if (!pane) return state;
      return {
        ...state,
        activePaneId: pane.id,
        activeTabKey: isRootPane(pane.id)
          ? pane.activeTabKey || state.activeTabKey
          : state.activeTabKey,
      };
    }

    case 'PIN_TAB': {
      const { key } = action.payload;
      const tab = state.tabs.find((t) => t.key === key);
      if (!tab || tab.pinned) return state;

      const maxPinnedOrder = Math.max(
        0,
        ...state.tabs.filter((t) => t.pinned).map((t) => t.pinnedOrder || 0)
      );

      return {
        ...state,
        tabs: sortTabs(
          state.tabs.map((t) =>
            t.key === key
              ? { ...t, pinned: true, closable: false, pinnedOrder: maxPinnedOrder + 1 }
              : t
          )
        ),
      };
    }

    case 'UNPIN_TAB': {
      const { key } = action.payload;
      const tab = state.tabs.find((t) => t.key === key);
      if (!tab || !tab.pinned) return state;

      return {
        ...state,
        tabs: sortTabs(
          state.tabs.map((t) =>
            t.key === key
              ? { ...t, pinned: false, closable: true, pinnedOrder: undefined }
              : t
          )
        ),
      };
    }

    case 'REORDER_TABS': {
      const { fromIndex, toIndex } = action.payload;
      if (fromIndex === toIndex) return state;

      const newTabs = [...state.tabs];
      const [movedTab] = newTabs.splice(fromIndex, 1);
      newTabs.splice(toIndex, 0, movedTab);

      // Update pinned order if reordering within pinned section
      const pinnedTabs = newTabs.filter((t) => t.pinned);
      const updatedTabs = newTabs.map((t) => {
        if (t.pinned) {
          const pinnedIndex = pinnedTabs.findIndex((pt) => pt.key === t.key);
          return { ...t, pinnedOrder: pinnedIndex };
        }
        return t;
      });

      return {
        ...state,
        tabs: updatedTabs,
      };
    }

    case 'NAVIGATE_IN_TAB': {
      const { path, search, state: routeState, title, replace } = action.payload;
      const paneId = action.payload.paneId || state.activePaneId;
      const activeTab = getActiveTabForPane(state, paneId);
      if (!activeTab) return state;

      // If navigating to same path, don't add to history
      const currentEntry = activeTab.history[activeTab.historyIndex];
      if (currentEntry?.path === path && currentEntry?.search === (search || '')) {
        return state;
      }

      const newEntry: TabHistoryEntry = {
        path,
        search: search || '',
        state: routeState,
        timestamp: Date.now(),
      };
      const newHistory = replace
        ? activeTab.history.map((entry, index) => index === activeTab.historyIndex ? newEntry : entry)
        : [...activeTab.history.slice(0, activeTab.historyIndex + 1), newEntry];

      // Limit history size
      const maxHistorySize = 50;
      if (newHistory.length > maxHistorySize) {
        newHistory.shift();
      }
      const nextHistoryIndex = replace
        ? Math.min(activeTab.historyIndex, newHistory.length - 1)
        : newHistory.length - 1;

      return {
        ...state,
        tabs: state.tabs.map((t) =>
          t.key === activeTab.key
            ? {
                ...t,
                path,
                title: title || t.title,
                history: newHistory,
                historyIndex: nextHistoryIndex,
                lastAccessed: Date.now(),
              }
            : t
        ),
        activePaneId: paneId,
        activeTabKey: isRootPane(paneId) ? activeTab.key : state.activeTabKey,
        panes: state.panes.map((pane) =>
          pane.id === paneId ? { ...pane, activeTabKey: activeTab.key } : pane
        ),
      };
    }

    case 'GO_BACK_IN_TAB': {
      const paneId = action.payload?.paneId || state.activePaneId;
      const activeTab = getActiveTabForPane(state, paneId);
      if (!activeTab || activeTab.historyIndex <= 0) return state;

      const newIndex = activeTab.historyIndex - 1;
      const entry = activeTab.history[newIndex];

      return {
        ...state,
        tabs: state.tabs.map((t) =>
          t.key === activeTab.key
            ? {
                ...t,
                path: entry.path,
                historyIndex: newIndex,
                lastAccessed: Date.now(),
              }
            : t
        ),
        activePaneId: paneId,
        activeTabKey: isRootPane(paneId) ? activeTab.key : state.activeTabKey,
      };
    }

    case 'GO_FORWARD_IN_TAB': {
      const paneId = action.payload?.paneId || state.activePaneId;
      const activeTab = getActiveTabForPane(state, paneId);
      if (!activeTab || activeTab.historyIndex >= activeTab.history.length - 1) return state;

      const newIndex = activeTab.historyIndex + 1;
      const entry = activeTab.history[newIndex];

      return {
        ...state,
        tabs: state.tabs.map((t) =>
          t.key === activeTab.key
            ? {
                ...t,
                path: entry.path,
                historyIndex: newIndex,
                lastAccessed: Date.now(),
              }
            : t
        ),
        activePaneId: paneId,
        activeTabKey: isRootPane(paneId) ? activeTab.key : state.activeTabKey,
      };
    }

    case 'UPDATE_TAB_TITLE': {
      const { key, title } = action.payload;
      return {
        ...state,
        tabs: state.tabs.map((t) => (t.key === key ? { ...t, title } : t)),
      };
    }

    case 'RESTORE_TABS': {
      const { tabs, activeTabKey } = action.payload;
      if (tabs.length === 0) {
        return getInitialState();
      }
      const sortedTabs = sortTabs(tabs);
      const panes = normalizePanes(sortedTabs, action.payload.panes, activeTabKey);
      const activePaneId = action.payload.activePaneId && panes.some((pane) => pane.id === action.payload.activePaneId)
        ? action.payload.activePaneId
        : panes.find((pane) => pane.activeTabKey === activeTabKey)?.id || panes[0]?.id || ROOT_PANE_ID;
      const activePane = panes.find((pane) => pane.id === activePaneId);
      return {
        ...state,
        tabs: sortedTabs,
        panes,
        activePaneId,
        splitOrientation: action.payload.splitOrientation || 'vertical',
        activeTabKey: activePane?.activeTabKey || activeTabKey || tabs[0]?.key || null,
      };
    }

    case 'REOPEN_CLOSED_TAB': {
      if (state.recentlyClosed.length === 0) return state;

      const [tabToReopen, ...remainingClosed] = state.recentlyClosed;

      // Check if tab already exists (by path)
      const existingTab = findTabByPath(state.tabs, tabToReopen.path);
      if (existingTab) {
        return {
          ...state,
          activeTabKey: existingTab.key,
          recentlyClosed: remainingClosed,
        };
      }

      // Reopen with new key
      const reopenedTab: TabItem = {
        ...tabToReopen,
        key: generateTabKey(tabToReopen.path),
        lastAccessed: Date.now(),
      };

      // Insert reopened tab right after the currently active tab
      const activeTabIndex = state.tabs.findIndex((t) => t.key === state.activeTabKey);
      const insertIndex = activeTabIndex === -1 ? state.tabs.length : activeTabIndex + 1;
      
      const newTabs = [...state.tabs];
      newTabs.splice(insertIndex, 0, reopenedTab);

      return {
        ...state,
        tabs: sortTabs(newTabs),
        activeTabKey: reopenedTab.key,
        panes: addTabToPane(state, state.activePaneId, reopenedTab.key, state.activeTabKey || undefined)
          .map((pane) => pane.id === state.activePaneId ? { ...pane, activeTabKey: reopenedTab.key } : pane),
        recentlyClosed: remainingClosed,
      };
    }

    case 'DUPLICATE_TAB': {
      const { key } = action.payload;
      const tabToDuplicate = state.tabs.find((t) => t.key === key);
      if (!tabToDuplicate) return state;
      const sourcePaneId = getPaneIdForTab(state, key);

      // Check max tabs limit
      if (state.tabs.length >= config.maxTabs) {
        // Close oldest non-pinned tab (excluding the one being duplicated)
        const unpinnedTabs = state.tabs.filter((t) => !t.pinned && t.key !== key);
        if (unpinnedTabs.length > 0) {
          const oldest = unpinnedTabs.reduce((a, b) =>
            a.lastAccessed < b.lastAccessed ? a : b
          );
          state = removeTabFromState(state, oldest, config);
        } else {
          // All tabs are pinned (or only the source tab is unpinned) - refuse to duplicate
          return state;
        }
      }

      const duplicatedTab = duplicateTab(tabToDuplicate);

      // Insert duplicated tab right after the original
      const originalIndex = state.tabs.findIndex((t) => t.key === key);
      const insertIndex = originalIndex === -1 ? state.tabs.length : originalIndex + 1;
      
      const newTabs = [...state.tabs];
      newTabs.splice(insertIndex, 0, duplicatedTab);

      return {
        ...state,
        tabs: sortTabs(newTabs),
        activeTabKey: isRootPane(sourcePaneId) ? duplicatedTab.key : state.activeTabKey,
        panes: addTabToPane(state, sourcePaneId, duplicatedTab.key, key)
          .map((pane) => pane.id === sourcePaneId ? { ...pane, activeTabKey: duplicatedTab.key } : pane),
        activePaneId: sourcePaneId,
      };
    }

    case 'SPLIT_TAB': {
      const { key, orientation } = action.payload;
      if (state.panes.length >= 2) {
        return {
          ...state,
          splitOrientation: orientation,
        };
      }

      const tabToSplit = state.tabs.find((tab) => tab.key === key);
      const sourcePane = state.panes.find((pane) => pane.tabKeys.includes(key));
      if (!tabToSplit || !sourcePane) return state;

      const now = Date.now();
      const splitPaneTab = tabToSplit;
      let nextTabs = state.tabs.map((tab) =>
        tab.key === key ? { ...tab, lastAccessed: now } : tab
      );
      const newPaneId = generatePaneId();
      const sourcePaneIndex = state.panes.findIndex((pane) => pane.id === sourcePane.id);
      const sourcePaneFallbackTabKey = getFallbackTabKeyForPane(sourcePane.tabKeys, key);
      const defaultTab = sourcePaneFallbackTabKey ? null : getDefaultTab();

      if (defaultTab) {
        nextTabs = sortTabs([...nextTabs, defaultTab]);
      }

      const panes = state.panes.map((pane) => {
        if (pane.id !== sourcePane.id) return pane;
        const nextSourceTabKeys = pane.tabKeys.filter((tabKey) => tabKey !== key);
        if (defaultTab) {
          nextSourceTabKeys.push(defaultTab.key);
        }
        return {
          ...pane,
          tabKeys: nextSourceTabKeys,
          activeTabKey: sourcePaneFallbackTabKey || defaultTab?.key || null,
        };
      });
      const newPane = createPane([splitPaneTab.key], splitPaneTab.key, newPaneId);
      panes.splice(sourcePaneIndex + 1, 0, newPane);

      return {
        ...state,
        tabs: sortTabs(nextTabs),
        panes,
        activePaneId: isRootPane(sourcePane.id) ? ROOT_PANE_ID : newPaneId,
        activeTabKey: isRootPane(sourcePane.id)
          ? sourcePaneFallbackTabKey || defaultTab?.key || state.activeTabKey
          : state.activeTabKey,
        splitOrientation: orientation,
      };
    }

    case 'TOGGLE_SPLIT': {
      const { key, orientation } = action.payload;
      const tabToToggle = state.tabs.find((tab) => tab.key === key);
      if (!tabToToggle) return state;

      if (state.panes.length > 1 && state.splitOrientation === orientation) {
        return collapsePanes(state, key);
      }

      if (state.panes.length > 1) {
        return {
          ...state,
          splitOrientation: orientation,
        };
      }

      const singlePaneState = state;
      const sourceTab = singlePaneState.tabs.find((tab) => tab.key === key)
        || singlePaneState.tabs.find((tab) => getTabLocationKey(tab) === getTabLocationKey(tabToToggle));
      if (!sourceTab) return singlePaneState;

      return tabsReducerWithConfig(singlePaneState, {
        type: 'SPLIT_TAB',
        payload: { key: sourceTab.key, orientation },
      }, config);
    }

    case 'MOVE_TAB_TO_PANE': {
      const { key, paneId, toIndex } = action.payload;
      const targetPane = getPane(state, paneId);
      const tab = state.tabs.find((item) => item.key === key);
      if (!targetPane || !tab) return state;

      const sourcePane = state.panes.find((pane) => pane.tabKeys.includes(key));
      if (!sourcePane) return state;

      const sourcePaneFallbackTabKey = getFallbackTabKeyForPane(sourcePane.tabKeys, key);
      const defaultTab = isRootPane(sourcePane.id) && !sourcePaneFallbackTabKey ? getDefaultTab() : null;
      const nextTabs = defaultTab && !state.tabs.some((item) => item.key === defaultTab.key)
        ? sortTabs([...state.tabs, defaultTab])
        : state.tabs;

      const panes = state.panes.map((pane) => {
        const withoutKey = pane.tabKeys.filter((tabKey) => tabKey !== key);
        if (pane.id === paneId) {
          const insertIndex = typeof toIndex === 'number'
            ? Math.max(0, Math.min(toIndex, withoutKey.length))
            : withoutKey.length;
          const nextTabKeys = [...withoutKey];
          nextTabKeys.splice(insertIndex, 0, key);
          return { ...pane, tabKeys: nextTabKeys, activeTabKey: key };
        }
        return {
          ...pane,
          tabKeys: defaultTab && pane.id === sourcePane.id ? [defaultTab.key] : withoutKey,
          activeTabKey: pane.activeTabKey === key
            ? (pane.id === sourcePane.id ? sourcePaneFallbackTabKey || defaultTab?.key || null : withoutKey[0] || null)
            : pane.activeTabKey,
        };
      }).filter((pane) => pane.tabKeys.length > 0);

      const activePaneId = isRootPane(paneId)
        ? ROOT_PANE_ID
        : isRootPane(sourcePane.id)
          ? ROOT_PANE_ID
          : paneId;
      const activeTabKey = isRootPane(paneId)
        ? key
        : isRootPane(sourcePane.id)
          ? sourcePaneFallbackTabKey || defaultTab?.key || nextTabs[0]?.key || null
          : key;

      return {
        ...state,
        tabs: nextTabs,
        panes: normalizePanes(nextTabs, panes, activeTabKey),
        activePaneId,
        activeTabKey,
      };
    }

    case 'CLOSE_PANE': {
      const { paneId } = action.payload;
      if (state.panes.length <= 1) return state;
      const pane = getPane(state, paneId);
      if (!pane) return state;

      const tabKeysToClose = new Set(pane.tabKeys);
      const closedTabs = state.tabs.filter((tab) => tabKeysToClose.has(tab.key) && !tab.pinned);
      let tabs = state.tabs.filter((tab) => !tabKeysToClose.has(tab.key) || tab.pinned);
      if (tabs.length === 0) {
        tabs = [getDefaultTab()];
      }
      const panes = normalizePanes(tabs, state.panes.filter((item) => item.id !== paneId), tabs[0]?.key || null);
      const fallbackPane = panes[0];

      return {
        ...state,
        tabs,
        panes,
        activePaneId: fallbackPane?.id || ROOT_PANE_ID,
        activeTabKey: fallbackPane?.activeTabKey || tabs[0]?.key || null,
        recentlyClosed: [...closedTabs, ...state.recentlyClosed].slice(0, config.maxRecentlyClosed),
      };
    }

    case 'SET_TAB_HISTORY_INDEX': {
      const { key, historyIndex } = action.payload;
      const tab = state.tabs.find((t) => t.key === key);
      if (!tab || historyIndex < 0 || historyIndex >= tab.history.length) return state;

      const entry = tab.history[historyIndex];
      return {
        ...state,
        tabs: state.tabs.map((t) =>
          t.key === key
            ? {
                ...t,
                path: entry.path,
                historyIndex,
                lastAccessed: Date.now(),
              }
            : t
        ),
        activePaneId: action.payload.paneId || getPaneIdForTab(state, key),
        activeTabKey: key,
        panes: state.panes.map((pane) =>
          pane.id === (action.payload.paneId || getPaneIdForTab(state, key))
            ? { ...pane, activeTabKey: key }
            : pane
        ),
      };
    }

    default:
      return state;
  }
}
